// Split a SKILL.md body into editable sections and compose them back.
//
// The body stays a single markdown string in Firestore — these helpers only
// power the two-mode editor (SkillBodyEditor.vue): "fields" mode shows the
// anatomy template below as separate textareas, "markdown" mode shows the raw
// string. Sections are verbatim line slices (heading line + following lines,
// untrimmed), so composeSections(parseSections(body)) === body whenever
// headings are preceded by a blank line — which is how both the AI author and
// fields mode write them.

// The anatomy of a skill body. `heading` is what compose writes; `match`
// tolerates the small variations the AI or a colleague may type. The intro has
// no heading: it's whatever precedes the first "## ".
export const TEMPLATE_SECTIONS = [
  {
    key: "intro",
    heading: null,
    label: "What this does",
    hint: "One plain sentence a brand-new hire would understand.",
  },
  {
    key: "steps",
    heading: "Steps",
    match: /^steps$/i,
    label: "Steps",
    hint: "The workflow, in the order you actually do it — with reasons.",
  },
  {
    key: "format",
    heading: "Format & output",
    match: /^format\s*(?:&|and)\s*output$/i,
    label: "Format & output",
    hint: "The shape of the result: length, tone, structure.",
  },
  {
    key: "always",
    heading: "Always do",
    match: /^always(?:\s+do)?$/i,
    label: "Always do",
    hint: "Your hard rules — one per line.",
  },
  {
    key: "never",
    heading: "Never do",
    match: /^never(?:\s+do)?$/i,
    label: "Never do",
    hint: "The mistakes you keep correcting.",
  },
  {
    key: "examples",
    heading: "Examples",
    match: /^examples?$/i,
    label: "Examples",
    hint: "Show, don't tell — paste a real good result (and a weak one).",
  },
  {
    key: "unsure",
    heading: "When unsure",
    match: /^when\s+unsure$/i,
    label: "When unsure",
    hint: "Ask before guessing. List questions, don't fill gaps silently.",
  },
];

// Template entry for an h2 heading text, or null when it's a custom section.
export function matchTemplate(heading) {
  if (!heading) return null;
  const h = heading.trim();
  return TEMPLATE_SECTIONS.find((t) => t.match && t.match.test(h)) || null;
}

const H2_RE = /^##\s+(.*)$/;
const FENCE_RE = /^\s{0,3}(?:```|~~~)/;

// Partition the body into verbatim slices on h2 headings (ignoring headings
// inside code fences). Returns [{ headingLine, heading, text, seeded: false }]
// where the first slice has headingLine === null (the intro) unless the body
// starts directly with a heading.
export function parseSections(body) {
  const lines = String(body || "").split("\n");
  const slices = [];
  let cur = { headingLine: null, heading: null, lines: [] };
  let inFence = false;

  for (const line of lines) {
    if (FENCE_RE.test(line)) inFence = !inFence;
    const h2 = !inFence && line.match(H2_RE);
    if (h2) {
      slices.push(cur);
      cur = { headingLine: line, heading: h2[1].trim(), lines: [] };
    } else {
      cur.lines.push(line);
    }
  }
  slices.push(cur);

  // Body starts directly with "## …": the intro slice is empty — drop it.
  if (slices[0].headingLine === null && slices[0].lines.length === 0) {
    slices.shift();
  }

  return slices.map((s) => ({
    headingLine: s.headingLine,
    heading: s.heading,
    text: s.lines.join("\n"),
    seeded: false,
  }));
}

// Inverse of parseSections. Seeded sections (empty template slots the editor
// added) only make it into the body once typed into, so an untouched new skill
// still composes to "". Between sections we keep whatever blank lines the text
// already carries; a freshly typed section (no trailing newline) gets a blank
// line inserted so the markdown stays readable.
export function composeSections(sections) {
  const parts = [];
  for (const s of sections || []) {
    if (s.seeded && !s.text.trim()) continue;
    if (s.headingLine != null) {
      parts.push(s.text === "" ? s.headingLine : `${s.headingLine}\n${s.text}`);
    } else {
      parts.push(s.text);
    }
  }
  let out = "";
  for (const part of parts) {
    if (!out) out = part;
    else out += (out.endsWith("\n") ? "\n" : "\n\n") + part;
  }
  return out;
}
